import React, {
  Fragment,
  Reducer,
  useEffect,
  useReducer,
  useState,
} from 'react'
import { FaRegEdit, FaTrashAlt } from 'react-icons/fa'
import { countBy, isEmpty, isEqual } from 'lodash'
import { v4 as uuid } from 'uuid'
import { useFormik } from 'formik'
import {
  Alert,
  Button,
  LabeledInput,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  useToast,
} from '@apexinternal/ui-components'
import {
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  HStack,
  Icon,
  Link,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { PhoneNumber, PhoneNumberType } from '../../../../types/CommonTypes'
import { formatPhoneNumber, restrictCharsForPhone } from '../../../../utils'
import { updatePhone } from '../../../../actions'
import { useToastErrorHandler } from '../../../../hooks'
import { updatePhoneSchema } from './UpdatePhoneSchema'

export interface PhoneNumberRow {
  rowId?: string
  phoneNumber?: string
  phoneNumberType?: PhoneNumberType
  extension?: string
}

interface UpdatePhoneModalProps {
  accountNumber: string
  phoneNumbers?: PhoneNumber[]
}

interface PhoneNumberValue {
  phoneNumber?: string
  phoneNumberType?: string
  extension?: string
}

enum FieldsEnum {
  PhoneNumberType = 'phoneNumberType',
  PhoneNumber = 'phoneNumber',
  Extension = 'extension',
}

type PhoneNumberAction = {
  type: 'ADD' | 'REMOVE' | 'UPDATE' | 'CLEAR'
  rowId?: string
  value?: string | PhoneNumberType
  field?: FieldsEnum
  payload?: PhoneNumberRow[]
}

const reducer: Reducer<PhoneNumberRow[], PhoneNumberAction> = (
  state: PhoneNumberRow[],
  action: PhoneNumberAction
) => {
  switch (action.type) {
    case 'ADD':
      return [
        ...state,
        {
          rowId: uuid(),
          phoneNumber: undefined,
          phoneNumberType: undefined,
          extension: undefined,
        },
      ]
    case 'REMOVE':
      return state.filter(item => item.rowId !== action.rowId)
    case 'UPDATE':
      return state.map(item => {
        if (item.rowId === action.rowId && action.field) {
          return { ...item, [action.field]: action.value }
        }
        return item
      })
    case 'CLEAR':
      return action.payload || []
    default:
      return state
  }
}

interface FieldTouched {
  [index: string]: boolean
}

const UpdatePhoneModal = ({
  accountNumber,
  phoneNumbers,
}: UpdatePhoneModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const onError = useToastErrorHandler()
  const toast = useToast()
  const formik = useFormik({
    initialValues: {
      phoneNumbers: [] as PhoneNumberRow[],
    },
    onSubmit: () => {
      onClose()
    },
    validationSchema: updatePhoneSchema,
  })
  const [fieldTouched, setFieldTouched] = useState<FieldTouched>({})

  const numbers: PhoneNumberRow[] = phoneNumbers?.length
    ? phoneNumbers.map(num => {
        return {
          ...num,
          rowId: uuid(),
        }
      })
    : []
  const [phoneNumberList, dispatch] = useReducer(reducer, numbers)

  const formikErrors = formik.errors.phoneNumbers as PhoneNumberRow[]

  let errorsMap: {
    [index: string]: {
      phoneNumberType?: string
      phoneNumber?: string
    }
  } = {}
  ;(formikErrors || []).forEach(
    (
      value: { phoneNumberType?: string; phoneNumber?: string },
      index: number
    ) => {
      if (value) {
        errorsMap[index] = {
          ...(value.phoneNumberType && {
            phoneNumberType: value.phoneNumberType,
          }),
          ...(value.phoneNumber && {
            phoneNumber: value.phoneNumber,
          }),
        }
      }
    }
  )

  const formatSubmission = (phoneNumbers: PhoneNumberRow[]) =>
    phoneNumbers.map(({ phoneNumber, phoneNumberType, extension }) => {
      return {
        phoneNumber: phoneNumber?.replace(/[(|)| |-]/g, ''),
        phoneNumberType,
        extension: extension || null,
      }
    }) as PhoneNumber[]

  // disables submit button if there is no data, no changes, or errors are present
  const isStateInvalid =
    isEmpty(phoneNumberList) ||
    isEqual(formatSubmission(phoneNumberList), formatSubmission(numbers)) ||
    !isEmpty(formik.errors)
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(
    isStateInvalid
  )

  const clearAndClose = () => {
    dispatch({ type: 'CLEAR', payload: numbers })
    setFieldTouched({})
    errorsMap = {}
    onClose()
  }

  const getError = (
    index: number,
    rowId: string,
    fieldName: 'phoneNumberType' | 'phoneNumber'
  ) => {
    const rowErrors = errorsMap[index]
    const fieldError =
      rowErrors && fieldTouched[`${fieldName}${rowId}`] && rowErrors[fieldName]
    return fieldError ? fieldError : undefined
  }

  const handleBlur = (
    fieldName: 'phoneNumber' | 'phoneNumberType',
    rowId: string
  ) => {
    setFieldTouched(prev => ({
      ...prev,
      [`${fieldName}${rowId}`]: true,
    }))
  }

  const handleSubmit = () => {
    const submission = formatSubmission(formik.values.phoneNumbers)
    updatePhone(accountNumber, submission, onError, () =>
      toast({
        title: 'Success!',
        description:
          'The phone number has been updated. Please allow up to 24 hours for this change to take effect.',
        status: 'success',
        isClosable: true,
      })
    )
    formik.submitForm()
  }

  useEffect(() => {
    formik.setFieldValue('phoneNumbers', phoneNumberList)
    // eslint-disable-next-line
  }, [phoneNumberList, formik.setFieldValue])

  useEffect(() => {
    setIsSubmitDisabled(isStateInvalid)
  }, [isStateInvalid, setIsSubmitDisabled])

  const typeCount = countBy(phoneNumberList, num => num.phoneNumberType)

  const titleSpan = 4
  const typeSpan = 3
  const numberSpan = 4
  const extensionSpan = 2
  const trashSpan = 1
  const totalColumnSpan =
    titleSpan + typeSpan + numberSpan + extensionSpan + trashSpan

  return (
    <>
      <Link data-testid="update-button" color="blue.500" onClick={onOpen}>
        <Icon as={FaRegEdit} marginRight="0.357rem" />
        Update
      </Link>
      <Modal isCentered isOpen={isOpen} onClose={clearAndClose}>
        <ModalOverlay />
        <ModalContent minW="500px">
          <ModalHeader>Update Phone Numbers</ModalHeader>
          <ModalCloseButton margin={2} />
          <ModalBody>
            <Text>Two phone numbers are allowed for each type.</Text>
            <Grid
              gridGap={3}
              padding="2rem 0 1rem 0"
              templateRows={`repeat(${phoneNumberList?.length}, fit-content 1fr)`}
              templateColumns={`repeat(${totalColumnSpan}, 1fr)`}
            >
              <GridItem
                colSpan={titleSpan}
                rowSpan={phoneNumberList?.length! + 1}
              >
                <Text>Phone Number</Text>
                <Button
                  data-testid="add-number-button"
                  mt={3}
                  variant="link"
                  isDisabled={phoneNumberList?.length === 8}
                  onClick={() => dispatch({ type: 'ADD' })}
                >
                  + Add Number
                </Button>
              </GridItem>
              <GridItem colSpan={typeSpan} hidden={!phoneNumberList?.length}>
                <Text>Type</Text>
              </GridItem>
              <GridItem colSpan={numberSpan} hidden={!phoneNumberList?.length}>
                <Text>Number</Text>
              </GridItem>
              <GridItem
                colSpan={extensionSpan + trashSpan}
                hidden={!phoneNumberList?.length}
              >
                <Text>Ext</Text>
              </GridItem>
              {phoneNumberList?.map(
                (
                  {
                    rowId,
                    phoneNumber,
                    phoneNumberType,
                    extension,
                  }: PhoneNumberRow,
                  index: number
                ) => {
                  const phoneNumberTypeErrorMessage = getError(
                    index,
                    rowId!,
                    'phoneNumberType'
                  )
                  const phoneNumberErrorMessage = getError(
                    index,
                    rowId!,
                    'phoneNumber'
                  )

                  return (
                    <Fragment key={rowId}>
                      <GridItem colSpan={typeSpan}>
                        <FormControl isInvalid={!!phoneNumberTypeErrorMessage}>
                          <Select
                            data-testid="phone-number-type-select"
                            name="phoneNumberType"
                            value={phoneNumberType || ''}
                            isInvalid={!!phoneNumberTypeErrorMessage}
                            onChange={e =>
                              dispatch({
                                type: 'UPDATE',
                                rowId,
                                field: FieldsEnum.PhoneNumberType,
                                value: e.target.value || undefined,
                              })
                            }
                            onBlur={() => handleBlur('phoneNumberType', rowId!)}
                          >
                            <option value="" />
                            <option
                              value={PhoneNumberType.Home}
                              hidden={typeCount[PhoneNumberType.Home] === 2}
                            >
                              Home
                            </option>
                            <option
                              value={PhoneNumberType.Cellular}
                              hidden={typeCount[PhoneNumberType.Cellular] === 2}
                            >
                              Cell
                            </option>
                            <option
                              value={PhoneNumberType.Business}
                              hidden={typeCount[PhoneNumberType.Business] === 2}
                            >
                              Business
                            </option>
                            <option
                              value={PhoneNumberType.Other}
                              hidden={typeCount[PhoneNumberType.Other] === 2}
                            >
                              Other
                            </option>
                          </Select>
                          <FormErrorMessage>
                            {phoneNumberTypeErrorMessage}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>
                      <GridItem colSpan={numberSpan}>
                        <FormControl isInvalid={!!phoneNumberErrorMessage}>
                          <LabeledInput
                            data-testid="phone-number-input"
                            name="phoneNumber"
                            value={formatPhoneNumber(phoneNumber!) || ''}
                            isInvalid={!!phoneNumberErrorMessage}
                            maxLength={14}
                            label=""
                            onChange={e =>
                              dispatch({
                                type: 'UPDATE',
                                rowId,
                                field: FieldsEnum.PhoneNumber,
                                value:
                                  formatPhoneNumber(
                                    restrictCharsForPhone(e.target.value)
                                  ) || undefined,
                              })
                            }
                            onBlur={() => handleBlur('phoneNumber', rowId!)}
                          />
                          <FormErrorMessage>
                            {phoneNumberErrorMessage}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>
                      <GridItem colSpan={extensionSpan}>
                        <LabeledInput
                          data-testid="extension-input"
                          name="extension"
                          value={extension || ''}
                          label=""
                          type="number"
                          onChange={e =>
                            dispatch({
                              type: 'UPDATE',
                              rowId,
                              field: FieldsEnum.Extension,
                              value: e.target.value || undefined,
                            })
                          }
                        />
                      </GridItem>
                      <GridItem colSpan={trashSpan}>
                        <Button
                          data-testid="remove-number-button"
                          onClick={() => dispatch({ type: 'REMOVE', rowId })}
                          variant="link"
                        >
                          <Icon as={FaTrashAlt} color="gray.400" h={6} w={6} />
                        </Button>
                      </GridItem>
                    </Fragment>
                  )
                }
              )}
            </Grid>
            <Alert
              title="Notice!"
              description="It may take up to 24 hours to reflect your changes."
              status="info"
              showIcon
            />
          </ModalBody>
          <ModalFooter>
            <HStack spacing={4}>
              <Button
                data-testid="cancel-button"
                autoFocus
                variant="white"
                onClick={clearAndClose}
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-button"
                variant="primary"
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
              >
                Submit Update
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default UpdatePhoneModal
